import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { Lot } from '@/types/fulfill/batches';

const DEFAULT_FILTERS_STATE: Record<string, string> = {
  isActive: 'active',
  expiresAfter: '',
  expiresBefore: '',
};

export const useBatches = () => {
  const [batches, setBatches] = useState<{
    data: Lot[];
    loading: boolean;
    totalRows: number;
  }>({
    data: [],
    loading: false,
    totalRows: 0,
  });

  const [openDrawer, setOpenDrawer] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [activeSearch, setActiveSearch] = useState<string>('');

  // Replicating OrderStatus filtering structures + Date parameters
  const [filtersState, setFiltersState] = useState<Record<string, string>>(DEFAULT_FILTERS_STATE);
  const [appliedFiltersState, setAppliedFiltersState] =
    useState<Record<string, string>>(DEFAULT_FILTERS_STATE);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);

  const handleOpenDrawer = () => setOpenDrawer(true);
  const handleCloseDrawer = () => setOpenDrawer(false);

  const onChgSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setActiveSearch(search);
    setPage(0);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const onChangeFilter = (name: string, value: string) => {
    const mappedName =
      name === 'startDate' ? 'expiresAfter' : name === 'endDate' ? 'expiresBefore' : name;
    setFiltersState((prev) => ({
      ...prev,
      [mappedName]: value,
    }));
  };

  const applyFilters = () => {
    setAppliedFiltersState(filtersState);
    setPage(0);
    setOpenDrawer(false);
  };

  const clearFilters = () => {
    const cleared = {
      isActive: DEFAULT_FILTERS_STATE.isActive,
      expiresAfter: '',
      expiresBefore: '',
    };
    setFiltersState(cleared);
    setAppliedFiltersState(cleared);
    setSearch('');
    setActiveSearch('');
    setPage(0);
  };

  const hasActiveFilters = () => {
    return (
      activeSearch.length > 0 ||
      appliedFiltersState.isActive !== DEFAULT_FILTERS_STATE.isActive ||
      appliedFiltersState.expiresAfter !== '' ||
      appliedFiltersState.expiresBefore !== ''
    );
  };

  const getIsActiveValue = (val: string): boolean | undefined => {
    if (val === 'active') return true;
    if (val === 'inactive') return false;
    return undefined;
  };

  const fetchLots = useCallback(async () => {
    setBatches((prev) => ({ ...prev, loading: true }));
    try {
      const http = initializeHttpProvider();
      const isActiveValue = getIsActiveValue(appliedFiltersState.isActive);

      const response = await http.lotTracking.getLots({
        page: page + 1,
        page_size: pageSize,
        isActive: isActiveValue,
        search: activeSearch.trim() || undefined,
        expiresAfter: appliedFiltersState.expiresAfter || undefined,
        expiresBefore: appliedFiltersState.expiresBefore || undefined,
      });

      if (response && response.success) {
        setBatches({
          data: response.lots || [],
          loading: false,
          totalRows: response.metadata?.total_records || 0,
        });
      } else {
        throw new Error('API response success is false');
      }
    } catch (error) {
      console.error('Failed to fetch lots:', error);
      toast.error('Ocurrió un error al cargar los lotes.');
      setBatches({
        data: [],
        loading: false,
        totalRows: 0,
      });
    }
  }, [page, pageSize, appliedFiltersState, activeSearch]);

  const downloadReport = useCallback(async () => {
    const http = initializeHttpProvider();
    const isActiveValue = getIsActiveValue(appliedFiltersState.isActive);

    return toast.promise(
      async () => {
        const response = await http.lotTracking.downloadLotsReport({
          isActive: isActiveValue,
          search: activeSearch.trim() || undefined,
          expiresAfter: appliedFiltersState.expiresAfter || undefined,
          expiresBefore: appliedFiltersState.expiresBefore || undefined,
        });
        if (!response.file_url) throw new Error('No se pudo descargar el reporte');
        window.open(response.file_url, '_blank');
      },
      {
        pending: 'Descargando reporte...',
        success: 'Reporte descargado',
        error: 'No se pudo descargar el reporte',
      },
    );
  }, [appliedFiltersState, activeSearch]);

  useEffect(() => {
    fetchLots();
  }, [fetchLots]);

  return {
    batches,
    openDrawer,
    search,
    activeSearch,
    filtersState,
    appliedFiltersState,
    page,
    pageSize,
    handleOpenDrawer,
    handleCloseDrawer,
    onChgSearch,
    onSearch,
    handleKeyPress,
    onChangeFilter,
    applyFilters,
    clearFilters,
    hasActiveFilters,
    setPage,
    setPageSize,
    refreshLots: fetchLots,
    downloadReport,
  };
};
